import { fmtDateTime, fmtDateTimeDeParede } from './formatters';
import type { StatusTone } from './statusColors';
import { ehSimulacao } from './jobDeAutomacao';

/**
 * O SELO DE ENVIO do Cadastrar (26/10/2026, decisões do Heitor: S1, V1, P1).
 *
 * O Heitor quer ver de relance quais licitações já tiveram a proposta ENVIADA e por onde. O sinal existia, mas era uma
 * pílula de 9 px ao lado de "Enviada", e não se percebia. Agora é um selo só, em duas linhas: o fato em cima, o como e
 * quando embaixo. V1: enviado é VERDE (feito é estado, e é o que se procura). O que falta é âmbar quando o prazo está a
 * menos de 24 h; o robô que falhou é vermelho, com o motivo.
 *
 * `submittedVia` (o backend grava ao enviar): `robo_<portal>` = o robô confirmou; `manual_flip` = alguém marcou no HUB;
 * `stub_monitoramento` = proposta só para ler mensagens, nunca enviada. Nulo numa enviada = legado, sem registro de por
 * onde (11 das 232 enviadas em 26/10).
 */
export type EstadoDoEnvio = 'portal' | 'mao' | 'legado' | 'monitor' | 'falhou' | 'nao_enviada' | 'outro';

export interface SeloDoEnvio {
  estado: EstadoDoEnvio;
  titulo: string;
  linha2: string;
  tom: StatusTone;
  /** O job que é a PROVA (P1): o envio confirmado, ou a tentativa que falhou. */
  jobDaProva?: any | null;
}

export interface EntradaDoSelo {
  proposal: any;
  /** Os jobs carregados pela tela (as 50 últimas automações), de qualquer proposta. */
  jobs: any[];
  agora?: number;
}

const NAO_ENVIADAS = new Set(['rascunho', 'aguardando_portal']);
const H = 3600 * 1000;

/** O envio confirmado pelo robô para esta proposta (o mais recente, de verdade, não simulação). */
export function jobDoEnvio(proposalId: number, jobs: any[]): any | null {
  return (jobs || []).find((j) => j?.proposalId === proposalId && j?.action === 'submit_proposal'
    && j?.status === 'completed' && !ehSimulacao(j)) || null;
}

/** "encerra em 18 h", "em 40 min", "encerrou". Só o tempo; a data vai ao lado. */
export function faltaPara(fim: number, agora: number): string {
  const d = fim - agora;
  if (d <= 0) return 'encerrou';
  if (d < H) return `encerra em ${Math.max(1, Math.round(d / 60000))} min`;
  if (d < 48 * H) return `encerra em ${Math.floor(d / H)} h`;
  return `encerra em ${Math.floor(d / (24 * H))} dias`;
}

/** O motivo do robô em uma linha curta ("BLL rejeitou a proposta: É necessário anexar…" → sem aspas, cortado). */
export function motivoCurto(msg: string | null | undefined, max = 70): string {
  const m = String(msg || '').replace(/["“”]/g, '').replace(/\s+/g, ' ').trim();
  if (!m) return 'sem motivo registrado';
  return m.length > max ? `${m.slice(0, max - 1)}…` : m;
}

export function seloDoEnvio({ proposal: p, jobs, agora = Date.now() }: EntradaDoSelo): SeloDoEnvio {
  const via = String(p?.submittedVia || '');
  // submittedAt é hora de Brasília num timestamp sem fuso (ver fmtDateTimeDeParede); o prazo e o job são instantes.
  const quando = p?.submittedAt ? fmtDateTimeDeParede(p.submittedAt) : null;

  if (via === 'stub_monitoramento') {
    return { estado: 'monitor', titulo: 'Só monitoramento', linha2: 'nunca foi enviada ao portal', tom: 'neutral' };
  }
  if (via.startsWith('robo')) {
    return {
      estado: 'portal',
      titulo: '✓ Enviada pelo portal',
      linha2: `robô${quando ? ` · ${quando}` : ''} · ver prova`,
      tom: 'success',
      jobDaProva: jobDoEnvio(p.id, jobs),
    };
  }
  if (via === 'manual_flip') {
    return { estado: 'mao', titulo: '✋ Enviada à mão', linha2: `marcada${quando ? ` em ${quando}` : ''} · sem prova`, tom: 'success' };
  }
  if (p?.status === 'enviada') {
    return { estado: 'legado', titulo: '✓ Enviada', linha2: 'sem registro de por onde', tom: 'success' };
  }

  if (NAO_ENVIADAS.has(p?.status)) {
    const fimRaw = p?.item?.dataEncerramento;
    const fim = fimRaw ? new Date(fimRaw).getTime() : NaN;
    const prazo = Number.isFinite(fim) ? `${faltaPara(fim, agora)} · ${fmtDateTime(fimRaw)}` : 'sem prazo de envio';
    // A tentativa que FALHOU é a última automação desta proposta, se for um envio: o selo diz o motivo.
    const ultimo = (jobs || []).find((j) => j?.proposalId === p.id) || null;
    if (ultimo && ultimo.action === 'submit_proposal' && ['failed', 'needs_manual'].includes(ultimo.status) && !ehSimulacao(ultimo)) {
      return {
        estado: 'falhou',
        titulo: 'Não enviada · o robô falhou',
        linha2: `${motivoCurto(ultimo.errorMessage)} · ${prazo}`,
        tom: 'danger',
        jobDaProva: ultimo,
      };
    }
    const urgente = Number.isFinite(fim) && fim > agora && fim - agora < 24 * H;
    return { estado: 'nao_enviada', titulo: 'Não enviada', linha2: prazo, tom: urgente ? 'warning' : 'neutral' };
  }

  return { estado: 'outro', titulo: '', linha2: '', tom: 'neutral' };
}

/** A trilha do robô, linha a linha, para a prova (P1). */
export function passosDaProva(job: any): Array<{ hora: string; texto: string; ok: boolean }> {
  const passos = Array.isArray(job?.steps) ? job.steps : [];
  return passos.map((s: any) => ({
    hora: String(s?.time || ''),
    texto: String(s?.name || s?.label || '').replace(/^envio API:\s*/i, ''),
    ok: s?.status === 'completed' || s?.status === 'done',
  }));
}
